import { Controller } from "@hotwired/stimulus"

/**
 * SpinShot Nav Controller
 * 
 * Manages the transparent-to-white navbar behavior on the SpinShot pages.
 * 
 * Features:
 * - Navbar is transparent when over dark sections (hero, testimonial, footer)
 * - Becomes white when over light sections
 * - Smooth transitions between states
 * 
 * Usage: Add data-controller="spinshot-nav" to the navbar element
 */
export default class extends Controller {
    connect() {
        this.handleScroll = this.handleScroll.bind(this)
        window.addEventListener('scroll', this.handleScroll, { passive: true })
        this.menu = this.element.querySelector('#topNavbar')
        this.handleMenuKeydown = this.handleMenuKeydown.bind(this)
        this.focusMenu = () => {
            if (window.innerWidth < 992) this.menu.querySelector('.btn-close-mobile')?.focus()
        }
        this.restoreMenuFocus = () => {
            if (window.innerWidth < 992) this.element.querySelector('.navbar-toggler')?.focus()
        }
        this.element.addEventListener('keydown', this.handleMenuKeydown)
        this.menu?.addEventListener('shown.bs.collapse', this.focusMenu)
        this.menu?.addEventListener('hidden.bs.collapse', this.restoreMenuFocus)

        // Check initial scroll position
        this.handleScroll()
    }

    disconnect() {
        window.removeEventListener('scroll', this.handleScroll)
        this.element.removeEventListener('keydown', this.handleMenuKeydown)
        this.menu?.removeEventListener('shown.bs.collapse', this.focusMenu)
        this.menu?.removeEventListener('hidden.bs.collapse', this.restoreMenuFocus)
    }

    handleMenuKeydown(event) {
        if (window.innerWidth >= 992 || !this.menu?.classList.contains('show')) return

        if (event.key === 'Escape') {
            event.preventDefault()
            window.bootstrap.Collapse.getOrCreateInstance(this.menu, { toggle: false }).hide()
        } else if (event.key === 'Tab') {
            const controls = [...this.menu.querySelectorAll('a[href], button:not([disabled])')]
                .filter(control => control.getClientRects().length > 0)
            const first = controls[0]
            const last = controls[controls.length - 1]
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault()
                last?.focus()
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault()
                first?.focus()
            }
        }
    }

    handleScroll() {
        const navbarHeight = this.element.offsetHeight
        const scrollY = window.scrollY
        const scrollPosition = scrollY + navbarHeight / 2
                const focusBand = document.querySelector('#industry-workflows > .focus-aside')?.getBoundingClientRect()
                this.element.classList.toggle('over-focus-band', !!focusBand && focusBand.top < navbarHeight && focusBand.bottom > 0)

        // At top of page (e.g. on load), default to over-dark so bar stays transparent until layout/scroll
        const atTop = scrollY < 20
        const hasHero = document.querySelector('.spinshot-hero')
        if (atTop && hasHero) {
            this.element.classList.add('over-dark')
            this.element.classList.remove('over-light')
            return
        }

        // Check if navbar is over a dark section
        const isDarkSection = this.isOverDarkSection(scrollPosition)

        this.element.classList.toggle('over-dark', isDarkSection)
        this.element.classList.toggle('over-light', !isDarkSection)
    }

    isOverDarkSection(position) {
        const navbarHeight = this.element.offsetHeight
        const viewportTop = window.scrollY
        const viewportBottom = viewportTop + window.innerHeight

        // For homepage/software page, find all dark sections (hero, testimonial, footer)
        const darkSections = document.querySelectorAll('.spinshot-hero, .seo-content-section, .testimonial-section, #industry-workflows > .focus-aside, .website-footer')

        for (const section of darkSections) {
            const rect = section.getBoundingClientRect()
            const sectionTop = rect.top + window.scrollY
            const sectionBottom = sectionTop + rect.height

            // Navbar overlaps section if viewport top (or navbar area) intersects the section
            // Use overlap: navbar/viewport top area [viewportTop, viewportTop + navbarHeight] vs section [sectionTop, sectionBottom]
            const navbarBottom = viewportTop + navbarHeight
            const overFocusBand = section.matches('#industry-workflows > .focus-aside')
            if (overFocusBand ? position >= sectionTop && position < sectionBottom : navbarBottom > sectionTop && viewportTop < sectionBottom) {
                return true
            }

            // On non-homepage www pages the footer may be shorter; treat "navbar over footer"
            // as dark whenever the footer overlaps the navbar area (top of viewport)
            if (section.classList.contains('website-footer')) {
                if (rect.top < navbarHeight && rect.bottom > 0) {
                    return true
                }
            }
        }

        return false
    }
}

